"use client";

import { useEffect } from "react";
import { getRum, initRum } from "@/lib/rum/client";
import { useRumPageViews } from "@/lib/hooks/useRumPageViews";
import { useAuthStore } from "@/lib/stores/useAuthStore";

/**
 * Initialises CloudWatch RUM and keeps it fed.
 *
 * Mounted ABOVE CognitoAuthProvider in the root layout on purpose:
 * CognitoAuthWrapper gates its whole subtree behind a `ready` state, so
 * anything mounted underneath records nothing during the auth handshake —
 * which is exactly when sign-in problems happen and exactly what we most
 * need telemetry for.
 *
 * No-ops entirely when RUM is not configured for the environment.
 */
export function RumProvider({ children }: { children: React.ReactNode }) {
  // Declared before useRumPageViews so this effect is registered — and
  // therefore runs — first: the client must exist before the initial page
  // view is recorded.
  useEffect(() => {
    initRum();
  }, []);

  useRumPageViews();

  const userProfile = useAuthStore((state) => state.user_profile);

  useEffect(() => {
    const rum = getRum();
    if (!rum || !userProfile) {
      return;
    }

    const attributes: Record<string, string | number | boolean> = {};

    // Cognito subject — a pseudonymous UUID, and the join key back to a user
    // when support needs to trace a reported problem to a session.
    if (userProfile.sub) {
      attributes.userId = userProfile.sub;
    }

    // orgid is a claim array. Send the primary plus a count rather than the
    // whole list: session attribute values must be scalars, and RUM caps how
    // many attributes a session may carry.
    const orgIds = userProfile.orgid;
    if (Array.isArray(orgIds) && orgIds.length > 0) {
      attributes.orgId = orgIds[0];
      attributes.orgCount = orgIds.length;
    }

    // Deliberately NOT sent: surgeon_npi. It is a real-world provider
    // identifier (PHI-adjacent), and telemetry is the wrong place for it.
    //
    // Also deliberately not calling pinUserId(sub): that would replace RUM's
    // anonymous cwr_u identifier with the Cognito subject, making the raw
    // telemetry more identifying than it needs to be.

    if (Object.keys(attributes).length === 0) {
      return;
    }

    try {
      rum.addSessionAttributes(attributes);
    } catch (error) {
      console.error("[rum] failed to add session attributes", error);
    }
  }, [userProfile]);

  return <>{children}</>;
}
